import { io, Socket } from 'socket.io-client';
import { usePermissionStore } from '@/stores/permissionStore';
import { API_CONFIG } from '@/config/api';

/** Notifications invalidate state; only the authenticated API grants permissions. */
class PermissionWebSocketService {
  private socket: Socket | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private debounce: ReturnType<typeof setTimeout> | null = null;
  private token: string | null = null;

  private refresh = () => {
    if (!this.token || this.token !== localStorage.getItem('token')) return;
    void usePermissionStore.getState().loadUserPermissions(true);
  };

  private scheduleRefresh = () => {
    if (this.debounce) clearTimeout(this.debounce);
    this.debounce = setTimeout(this.refresh, 100);
  };

  private sessionChanged = (event: StorageEvent) => {
    if (event.key !== 'token' || event.oldValue === event.newValue) return;
    this.disconnect();
    usePermissionStore.getState().reset();
    if (event.newValue) this.connect();
  };

  private resume = () => {
    if (document.hidden || !navigator.onLine) return;
    this.socket?.connect();
    this.scheduleRefresh();
  };

  connect(): void {
    const token = localStorage.getItem('token');
    if (!token) return;
    if (this.socket && this.token === token) return;
    this.disconnect();
    this.token = token;
    const baseUrl =
      process.env.REACT_APP_WS_URL || API_CONFIG.getBaseURL() || window.location.origin;
    this.socket = io(`${baseUrl.replace(/\/$/, '')}/order-updates`, {
      auth: { token },
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionDelayMax: 30000,
    });
    this.socket.on('connect', () => {
      // Subscribe only to the authenticated permission room (no duplicate user-room events).
      this.socket?.emit('subscribe-permissions', {}, () => this.scheduleRefresh());
      this.scheduleRefresh();
    });
    ['permission-update', 'role-update', 'team-change'].forEach((event) => {
      this.socket?.on(event, this.scheduleRefresh);
    });
    this.socket.on('permission-structure-update', () => {
      void usePermissionStore.getState().loadPermissionGroups(true);
      this.scheduleRefresh();
    });
    // Catch missed events, proxy failures and changes made through another backend process.
    this.timer = setInterval(() => {
      if (!document.hidden && navigator.onLine) this.scheduleRefresh();
    }, 30000);
    window.addEventListener('permissions-invalidated', this.scheduleRefresh);
    window.addEventListener('storage', this.sessionChanged);
    window.addEventListener('online', this.resume);
    window.addEventListener('focus', this.resume);
    document.addEventListener('visibilitychange', this.resume);
    window.addEventListener('admin-auth-cleared', this.disconnect);
  }

  disconnect = (): void => {
    this.token = null;
    this.socket?.removeAllListeners();
    this.socket?.disconnect();
    this.socket = null;
    if (this.timer) clearInterval(this.timer);
    if (this.debounce) clearTimeout(this.debounce);
    this.timer = null;
    this.debounce = null;
    window.removeEventListener('permissions-invalidated', this.scheduleRefresh);
    window.removeEventListener('storage', this.sessionChanged);
    window.removeEventListener('online', this.resume);
    window.removeEventListener('focus', this.resume);
    document.removeEventListener('visibilitychange', this.resume);
    window.removeEventListener('admin-auth-cleared', this.disconnect);
  };
}

export const permissionWebSocket = new PermissionWebSocketService();
